import {
  getClientCredentials,
  getExtensionClientCredentials,
  getMobileClientCredentials,
} from '../constants';
import { getApi } from '.';
import { getCredentialsStore } from '../storage/stores';
import {
  UserPasswordAccessTokenRequestBody,
  AuthorizationTokenAccessTokenRequestBody,
  DelegationAccessTokenRequestBody,
} from '../types/api';

export const setApiCredentials = (params: {
  accessToken: string | undefined;
  refreshToken: string | undefined;
  clientCredentials: { client_id: string; client_secret: string };
  onTokensChange: (params: {
    accessToken: string;
    refreshToken: string;
  }) => void;
}) => {
  const { accessToken, refreshToken, clientCredentials, onTokensChange } =
    params;
  getApi().setGlobals({
    tokens: { accessToken, refreshToken },
    clientCredentials,
    saveTokensCallback: (accessToken: string, refreshToken: string) => {
      onTokensChange({ accessToken, refreshToken });
    },
  });
};

export const authenticateByAuthorizationToken = async (
  authorizationToken: string
) =>
  fetchAndStoreTokens({
    grant_type: 'authorization_code',
    code: authorizationToken,
    ...getExtensionClientCredentials(),
  });

/*
 * Authenticate using a username and password, and store the tokens.
 */
export const authenticateByPassword = async (
  username: string,
  password: string
) =>
  fetchAndStoreTokens({
    grant_type: 'password',
    username: username,
    password: password,
    ...getMobileClientCredentials(),
  });

/*
 * Authenticate using a JWT delegation token.
 *
 * @returns the ID of the profile that's being onboarded.
 */
export const authenticateByDelegationToken = async (token: string) => {
  const clientId = getClientCredentials().client_id;
  const clientSecret = getClientCredentials().client_secret;
  const requestBody: DelegationAccessTokenRequestBody = {
    client_id: clientId,
    client_secret: clientSecret,
    external_token: token,
  };

  // Call QAPI authentication endpoint
  const {
    access_token: accessToken,
    refresh_token: refreshToken,
    profile_id: profileId,
  } = await getApi().delegationAccessToken.post(requestBody);

  saveCredentials(clientId, clientSecret, accessToken, refreshToken);

  return profileId;
};

const fetchAndStoreTokens = async (
  requestBody:
    | UserPasswordAccessTokenRequestBody
    | AuthorizationTokenAccessTokenRequestBody
) => {
  const { access_token: accessToken, refresh_token: refreshToken } =
    await getApi().accessToken.post(requestBody);

  saveCredentials(
    requestBody.client_id,
    requestBody.client_secret,
    accessToken,
    refreshToken
  );
};

/**
 * Sets the credentials to QAPI client and persists them for future use.
 */
const saveCredentials = (
  clientId: string,
  clientSecret: string,
  accessToken: string,
  refreshToken: string
) => {
  const store = getCredentialsStore();
  const clientCredentials = {
    client_id: clientId,
    client_secret: clientSecret,
  };
  setApiCredentials({
    accessToken,
    refreshToken,
    clientCredentials,
    onTokensChange: store.refreshTokens,
  });
  store.set({ accessToken, refreshToken });
};
